/**
 * License utility functions — source of truth for license enforcement logic.
 * Used by both frontend (Redux selectors) and referenced by Cloud Functions.
 */

/**
 * Computes derived status fields for a license document.
 * Never stored in DB — always computed fresh.
 */
export const computeLicenseStatus = (license) => {
  if (!license) return null;
  const now = new Date();
  const trialEndsAt = license.trialEndsAt?.toDate
    ? license.trialEndsAt.toDate()
    : license.trialEndsAt instanceof Date
    ? license.trialEndsAt
    : license.trialEndsAt
    ? new Date(license.trialEndsAt)
    : null;

  const isTrialExpired = license.plan === 'trial' && trialEndsAt && now > trialEndsAt;
  const isExpired = license.status === 'expired' || isTrialExpired;
  const isActive = license.status === 'active' && !isExpired;

  const daysRemainingInTrial =
    license.plan === 'trial' && trialEndsAt && !isTrialExpired
      ? Math.max(0, Math.ceil((trialEndsAt - now) / 86400000))
      : null;

  return { ...license, isActive, isExpired, isTrialExpired, daysRemainingInTrial };
};

/**
 * Returns the effective limit for a given key,
 * checking org-specific overrides before the plan definition.
 */
export const getEffectiveLimit = (planDef, license, limitKey) => {
  const override = license?.overrides?.limits?.[limitKey];
  if (override !== undefined && override !== null) return override;
  return planDef?.limits?.[limitKey] ?? 9999;
};

/**
 * Checks whether a widget is allowed for the current license.
 * All widgets are available in all plans.
 * Only org-level overrides (block) can restrict access.
 */
export const isWidgetAllowed = (planDefinitions, license, widget) => {
  if (!license || !widget) return false;
  const inBlock = license.overrides?.widgetOverrides?.block?.includes(widget.componentKey);
  if (inBlock) return false;
  return true;
};

/**
 * Returns true if value represents "unlimited" (9999 sentinel).
 */
export const isUnlimited = (val) => val >= 9999;

/**
 * Static plan level map — used when planDefinitions are unavailable (e.g., public display).
 * trial has the same level as basic (same limits).
 */
export const PLAN_LEVEL_MAP = {
  trial: 1,
  basic: 1,
  pro: 2,
  community: 3,
};

/**
 * Returns the array of screenIds that are "suspended" after a downgrade.
 * The oldest N screens (by createdAt) remain active; newer ones are suspended.
 * @param {Array} screens
 * @param {number} effectiveScreensLimit
 * @returns {string[]}
 */
export const getSuspendedScreenIds = (screens, effectiveScreensLimit) => {
  if (!screens || screens.length === 0) return [];
  if (isUnlimited(effectiveScreensLimit) || screens.length <= effectiveScreensLimit) return [];
  const sorted = [...screens].sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
    return dateA - dateB; // oldest first — they stay active
  });
  return sorted.slice(effectiveScreensLimit).map(s => s.screenId);
};

/**
 * Computes the impact of a plan downgrade.
 * Returns null if it's not a downgrade (same plan or upgrade).
 * @param {Object} planDefinitions
 * @param {string} fromPlan
 * @param {string} toPlan
 * @returns {Object|null}
 */
export const computeDowngradeImpact = (planDefinitions, fromPlan, toPlan) => {
  if (!planDefinitions || !fromPlan || !toPlan || fromPlan === toPlan) return null;
  const fromDef = planDefinitions[fromPlan];
  const toDef = planDefinitions[toPlan];
  if (!fromDef || !toDef) return null;
  const fromLevel = fromDef.planLevel ?? 0;
  const toLevel = toDef.planLevel ?? 0;
  if (toLevel >= fromLevel) return null; // upgrade or same
  return {
    fromPlan,
    toPlan,
    limits: {
      screens:               { from: fromDef.limits?.screens               ?? 9999, to: toDef.limits?.screens               ?? 9999 },
      savedWidgets:          { from: fromDef.limits?.savedWidgets          ?? 9999, to: toDef.limits?.savedWidgets          ?? 9999 },
      versionHistory:        { from: fromDef.limits?.versionHistory        ?? 9999, to: toDef.limits?.versionHistory        ?? 9999 },
      playlistsPerScreen:    { from: fromDef.limits?.playlistsPerScreen    ?? 9999, to: toDef.limits?.playlistsPerScreen    ?? 9999 },
      dashboardsPerPlaylist: { from: fromDef.limits?.dashboardsPerPlaylist ?? 9999, to: toDef.limits?.dashboardsPerPlaylist ?? 9999 },
    },
  };
};
